import _ from "lodash";

import { hasExpiredBackgroundReports } from "manager/hooks/application-summary/utils";
import { normalizeOutcomesToArray } from "shared/components/vega/LumenV2/utils";
import {
  deriveSentilinkView,
  SentilinkRiskTierKey,
} from "shared/components/vega/ApplicantSummaryV2/sentilinkIdentityLines";
import { isPersonaIncomplete } from "shared/utils/persona";

import {
  buildApplicationCategoryDetailsLink,
  CategoryId,
  categoryGroups,
  CATEGORY_ENDPOINTS_V2,
  EXPIRED_BACKGROUND_REPORTS_MESSAGE,
  issueDetectors,
} from "../../constants";
import {
  getIncomeAndCashRunwayData,
  isBackgroundReportIncomplete,
} from "../../queryHelpers";
import { StatusType } from "../../StatusIcon";

import { ModalSection } from "../components/ApplicantDetailModal";
import {
  ApplicantCardDetailsProps,
  IdentityFraudRiskCardData,
} from "../components/ApplicantGroupCard";

import {
  getCreditHistoryData,
  getCriminalHistoryData,
  getRentalHistoryData,
} from "../helpers/applicantHistoryHelpers";

import {
  getIdVerificationModalDetails,
  getIncomeModalDetails,
  getCreditHistoryModalDetails,
  getRentalHistoryModalDetails,
  getCriminalHistoryModalDetails,
} from "./applicantSummaryDetailHelpers";
import {
  formatVerifiedAnnualIncomeDisplay,
  formatVerifiedMonthlyIncomeDisplay,
} from "../../helpers/incomeDisplayHelpers";

/**
 * Helper functions to determine the status of different parts of an application
 */

/**
 * Get identity status based on lumen results and identity data
 */
export const getIdentityApplicantSummary = (
  legacyApp: any
): {
  documentDetails: string;
} => {
  const identityData = legacyApp?.identity;
  const identitySkipped = legacyApp?.identity?.skipped;

  let documentDetails = "Pending";

  if (identityData?.documentType) {
    documentDetails = `${identityData.country} ${identityData.documentType}`;
  } else if (identitySkipped) {
    documentDetails = "Skipped";
  }

  return {
    documentDetails,
  };
};

/**
 * Get credit status based on lumen results and credit data
 */
export const getCreditApplicantSummary = (
  applicantLumenResult: any,
  application: any,
  isLumenAvailable: boolean,
  isBackgroundCreditCheckSkipped: boolean,
  isThinCreditFile: boolean | undefined,
  isLowCreditScore: boolean | undefined
): {
  creditScore: string;
  bankruptcies: string;
  collections: string;
  foreclosures: string;
} => {
  const { backgroundCreditCheck, backgroundCreditCheckCompleted } =
    application || {};
  const { creditScoreSummary } = backgroundCreditCheck || {};
  const creditHistoryData = getCreditHistoryData(
    applicantLumenResult,
    creditScoreSummary
  );

  const rawScore = creditScoreSummary?.score;
  const hasCreditScore =
    (creditScoreSummary != null &&
      (rawScore === null || rawScore === undefined)) ||
    (!_.isNil(rawScore) && rawScore !== "");

  let creditScoreDisplayValue = "Pending";
  if (isBackgroundCreditCheckSkipped) {
    creditScoreDisplayValue = "Skipped";
  } else if (hasCreditScore) {
    const baseScore = rawScore === null || rawScore === undefined ? 0 : rawScore;
    let creditScore = `${baseScore}`;
    if (isThinCreditFile) {
      creditScore = `${creditScore} (Thin Credit File)`;
    } else if (isLowCreditScore) {
      creditScore = `${creditScore} (Low Credit Score)`;
    }
    creditScoreDisplayValue = creditScore;
  }
  let bankruptcies = "Pending";
  if (isBackgroundCreditCheckSkipped) {
    bankruptcies = "Skipped";
  } else if (isLumenAvailable) {
    bankruptcies = creditHistoryData.bankruptcies.hasRecords ? "Yes" : "No";
  }
  let collections = "Pending";
  if (isBackgroundCreditCheckSkipped) {
    collections = "Skipped";
  } else if (backgroundCreditCheckCompleted) {
    collections = creditHistoryData.collections.hasRecords ? "Yes" : "No";
  }

  let foreclosures = "Pending";
  if (isBackgroundCreditCheckSkipped) {
    foreclosures = "Skipped";
  } else if (isLumenAvailable) {
    foreclosures = creditHistoryData.foreclosures.hasRecords ? "Yes" : "No";
  }
  return {
    creditScore: creditScoreDisplayValue,
    bankruptcies,
    collections,
    foreclosures,
  };
};

/**
 * Get rental history status based on lumen results and rental history data
 */
export const getRentalApplicantSummary = (
  applicantLumenResult: any,
  application: any,
  isLumenAvailable: boolean,
  isBackgroundCreditCheckSkipped: boolean
): {
  evictionRecords: string;
  rentalCollections: string;
} => {
  const { backgroundCreditCheck, backgroundCreditCheckCompleted } =
    application || {};
  const { evictionRecords: applicantEvictionRecords } =
    backgroundCreditCheck || {};
  const rentalHistoryData = getRentalHistoryData(
    applicantLumenResult,
    applicantEvictionRecords?.evictionRecordGroup
  );

  let evictionRecords = "Pending";
  if (isBackgroundCreditCheckSkipped) {
    evictionRecords = "Skipped";
  } else if (backgroundCreditCheckCompleted) {
    evictionRecords = rentalHistoryData.evictionRecords.hasRecords
      ? "Yes"
      : "No";
  }
  let rentalCollections = "Pending";
  if (isBackgroundCreditCheckSkipped) {
    rentalCollections = "Skipped";
  } else if (isLumenAvailable) {
    rentalCollections = rentalHistoryData.rentalCollections.hasIssue
      ? "Yes"
      : "No";
  }

  return {
    evictionRecords,
    rentalCollections,
  };
};

/**
 * Get criminal history status based on lumen results and criminal history data
 */
export const getCriminalApplicantSummary = (
  application: any,
  isBackgroundCreditCheckSkipped: boolean,
  isConditionalProceedPending: boolean = false
): {
  criminalRecords: string;
} => {
  if (isConditionalProceedPending) {
    return {
      criminalRecords: "Pending",
    };
  }

  const { backgroundCreditCheck, backgroundCreditCheckCompleted } =
    application || {};
  const { criminalRecords: applicantCriminalRecords } =
    backgroundCreditCheck || {};
  const criminalHistoryData = getCriminalHistoryData(
    applicantCriminalRecords?.criminalRecordGroup
  );
  let criminalRecords = "Pending";
  if (isBackgroundCreditCheckSkipped) {
    criminalRecords = "Skipped";
  } else if (backgroundCreditCheckCompleted) {
    criminalRecords = criminalHistoryData.criminalRecords.hasRecords
      ? "Yes"
      : "No";
  }

  return {
    criminalRecords,
  };
};

/**
 * Convert all application statuses to a format compatible with ApplicantCardDetails
 */
export const formatCardDetails = ({
  title,
  status,
  details,
  allDetailsLink,
  cardModalDetails,
  identityFraudRisk,
}: {
  title: string;
  status: StatusType;
  details: Array<{ label?: string; value: string; status?: string }>;
  allDetailsLink?: string;
  cardModalDetails?: ModalSection[];
  identityFraudRisk?: IdentityFraudRiskCardData;
}): ApplicantCardDetailsProps => {
  return {
    status: status as StatusType,
    cardTitle: title,
    cardDetails: details.map((detail) => ({
      label: detail.label || "",
      value: detail.value,
      status: detail.status === "pass" ? null : detail.status,
    })),
    allDetailsLink,
    cardModalDetails,
    identityFraudRisk,
  };
};

/**
 * Format income value for display based on submission status
 */
export const formatIncomeBalanceDisplay = (
  income: number | null | undefined,
  type: "Income" | "Balance",
  isSubmitted: boolean
): string => {
  if (income === 0 || income === undefined || income === null) {
    if (!isSubmitted) {
      return "Pending";
    }
    return `${type} could not be verified`;
  }

  // Format income as dollars with cents
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(income);
};

// Helper for checking status of a specific issue type
export const getIssueStatus = (
  issueType: string,
  label: string,
  applicantLumenResult: any,
  applicationLumenResult: any,
  lumenRun: any,
  isLumenAvailable: boolean
) => {
  const detector = issueDetectors[issueType].find((d) => d.label === label);

  if (!detector || !isLumenAvailable) return undefined;

  const outcomes = normalizeOutcomesToArray(lumenRun?.outcomes);

  const matchingOutcomes = outcomes.filter((outcome) => {
    const outcomeName = outcome?.name || outcome?.formula;
    return detector.keywords.every((keyword) => {
      return outcomeName?.toLowerCase().includes(keyword.toLowerCase());
    });
  });

  if (matchingOutcomes.length === 0) return undefined;

  const applicantTriggeredOutcomes = _.get(
    applicantLumenResult,
    "evaluation.triggeredOutcomes",
    []
  );

  const triggeredOutcomeNames = applicantTriggeredOutcomes
    .map((outcome) => (typeof outcome === "string" ? outcome : outcome?.name))
    .filter(Boolean);

  const applicationTriggeredOutcomes =
    applicationLumenResult?.evaluation?.triggeredOutcomes || [];

  const matchingTriggeredOutcomes = matchingOutcomes.filter((outcome) => {
    const outcomeName = outcome?.name || outcome?.formula;
    return (
      triggeredOutcomeNames.some(
        (name) => name === outcomeName || name.includes(outcomeName)
      ) ||
      applicationTriggeredOutcomes.some(
        (name) => name === outcomeName || name.includes(outcomeName)
      )
    );
  });

  if (matchingTriggeredOutcomes.length === 0) {
    return undefined;
  }

  // Prefer fail over warn over pass when several triggered outcomes match the
  // same detector (e.g. identity-verification fail + another identity-* warn).
  // .find() previously returned whichever outcome appeared first in lumenRun.
  const colors = matchingTriggeredOutcomes
    .map((outcome) => outcome.scoreLabelColor?.toLowerCase())
    .filter(Boolean);
  if (colors.includes("fail")) return "fail";
  if (colors.includes("warn")) return "warn";
  return colors[0];
};

// Helper for determining overall category status
export const determineCategoryStatus = (
  categoryId: string,
  specificStatuses: string[],
  backgroundReportIncomplete: boolean,
  isLumenAvailable: boolean,
  lumenRun: any
) => {
  if (!isLumenAvailable || !lumenRun) {
    return "pending";
  }

  let status = "pass";

  const validStatuses = specificStatuses.filter(Boolean);

  if (validStatuses.length > 0) {
    if (validStatuses.includes("fail")) status = "fail";
    else if (validStatuses.includes("warn")) status = "warn";
  }

  if (backgroundReportIncomplete) {
    const backgroundCategories = [
      CategoryId.CREDIT_HISTORY,
      CategoryId.RENTAL_HISTORY,
      CategoryId.CRIMINAL_HISTORY,
    ];

    if (
      backgroundCategories.includes(categoryId as CategoryId) &&
      status === "pass"
    ) {
      status = "warn";
    }
  }

  return status;
};

/** Card tier color per recognized SentiLink risk tier (PRODUCT-1346). */
const TIER_CARD_STATUS: Record<SentilinkRiskTierKey, StatusType> = {
  HIGH_RISK: "fail",
  NEEDS_REVIEW: "warn",
  LOW_RISK: "pass",
};

/**
 * Build the structured Identity Risk Check payload for the ID Verification
 * card (PRODUCT-1346): tier label, blocked/error state, reason descriptions, the
 * Government ID & Selfie (Persona) status, and the reset affordance metadata.
 * Returns undefined when SentiLink data is unavailable so non-SentiLink deals are
 * unaffected.
 */
const buildIdentityFraudRiskData = (
  legacyApp: any
): IdentityFraudRiskCardData | undefined => {
  const view = deriveSentilinkView(legacyApp?.sentilink);
  if (!view) {
    return undefined;
  }

  let tierLabel: string | null = null;
  let tierStatus: StatusType | null = null;
  if (view.isError) {
    // An errored check is a soft warning (orange), not a hard failure, per the
    // SentiLink verifications UX (PRODUCT-1282 AC: "ERROR shows orange/warn").
    tierLabel = "Error";
    tierStatus = "warn";
  } else if (view.triggered && view.tierKey) {
    tierLabel = view.tierLabel;
    tierStatus = TIER_CARD_STATUS[view.tierKey];
  } else if (view.notTriggered === "SKIPPED") {
    tierLabel = "Skipped (no SSN)";
    tierStatus = "pending";
  } else if (view.notTriggered === "NOT_STARTED") {
    tierLabel = "Not started";
    tierStatus = "pending";
  }
  // AWAITING (and any unrecognized triggered state) leaves the tier blank; the
  // in-card block renders the "Awaiting re-submission" message instead.

  const { documentDetails } = getIdentityApplicantSummary(legacyApp);
  const documentCaptured =
    documentDetails !== "Pending" && documentDetails !== "Skipped";
  const governmentIdVerified =
    legacyApp?.identity?.verified === true && documentCaptured;
  const isManuallyVerified = view.isManuallyVerified === true;
  const sentilinkScore = legacyApp?.sentilink?.score;
  const sentilinkOutcome = String(
    legacyApp?.sentilink?.outcome ?? ""
  ).toUpperCase();
  const sentilinkResponseStatus = String(
    legacyApp?.sentilink?.responseStatus ?? ""
  ).toUpperCase();
  const isFailedVerifiable =
    view.triggered &&
    !view.isError &&
    sentilinkResponseStatus !== "FAILURE" &&
    typeof sentilinkScore === "number" &&
    (sentilinkOutcome === "BLOCK" || sentilinkScore >= 750);

  const nameMismatchDetails = legacyApp?.sentilink?.nameMismatchDetails;
  const hasNameMismatch =
    legacyApp?.sentilink?.nameIdentityMismatch === true &&
    !isManuallyVerified;
  let nameMismatch:
    | {
        idCredentialName: string;
        submittedName: string;
        anchorSource?: string | null;
      }
    | undefined;
  if (hasNameMismatch && nameMismatchDetails) {
    const idCredentialName = nameMismatchDetails.idCredentialName?.trim();
    const submittedName = nameMismatchDetails.submittedName?.trim();
    if (idCredentialName && submittedName) {
      nameMismatch = {
        idCredentialName,
        submittedName,
        anchorSource: nameMismatchDetails.anchorUsed ?? null,
      };
    }
  }
  if (hasNameMismatch && nameMismatch) {
    tierLabel = "Name mismatch";
    tierStatus = "fail";
  }

  return {
    applicationId: legacyApp?.appId ?? legacyApp?.info?.applicationId,
    providerSentilinkId: legacyApp?.sentilink?.recordId ?? null,
    triggered: view.triggered,
    isBlocked: view.isBlocked && !isManuallyVerified,
    isError: view.isError,
    isNeedsReview:
      view.triggered &&
      view.tierKey === "NEEDS_REVIEW" &&
      !view.isBlocked &&
      !view.isError &&
      !isManuallyVerified,
    isFailedVerifiable: isFailedVerifiable && !isManuallyVerified,
    isManuallyVerified,
    tierLabel: isManuallyVerified ? "Manually verified" : tierLabel,
    originalTierLabel: tierLabel,
    tierStatus: isManuallyVerified ? "pass" : tierStatus,
    reasons: isManuallyVerified ? [] : view.reasons,
    awaitingResubmission: view.notTriggered === "AWAITING",
    resetCount: view.resetCount,
    resetLimit: view.resetLimit,
    applicantEmail: legacyApp?.info?.email,
    checkedAt: legacyApp?.sentilink?.calledAt ?? null,
    manualVerification: view.manualVerification,
    governmentId: {
      verified: governmentIdVerified,
      label: governmentIdVerified ? documentDetails : null,
    },
    ...(nameMismatch ? { nameMismatch } : {}),
  };
};

/**
 * Resolve applicant ID Verification card chrome from SentiLink fraud-risk data
 * and optional Lumen identity status.
 *
 * Precedence (PRODUCT-1346 / 1282 / 2164 / 2429 follow-up):
 * 1. BLOCK → fail
 * 2. Manual fraud-risk verify → pass
 * 3. Awaiting re-submission after reset → pending (ignore stale Lumen fail)
 * 4. Lumen identity fail → fail (even when tier is Needs Review warn)
 * 5. Tier pass → pass only if Government ID verified, else pending
 * 6. Other tier statuses (warn/fail) → that tier
 *
 * @param identityFraudRisk - Structured fraud-risk payload, if SentiLink ran
 * @param lumenIdStatus - Category status from Lumen identity outcomes when known
 * @returns Chrome status, or undefined when there is no fraud-risk payload
 */
const getIdCardChromeStatus = (
  identityFraudRisk: IdentityFraudRiskCardData | undefined,
  lumenIdStatus?: StatusType | string
): StatusType | undefined => {
  if (!identityFraudRisk) {
    return undefined;
  }
  // A blocked check is a hard failure: drive the card chrome to "fail" so it
  // matches the red BLOCKED pill (PRODUCT-1346). An errored check is a soft
  // "warn" (orange) per PRODUCT-1282, so it falls through to its tier status.
  if (identityFraudRisk.isBlocked) {
    return "fail";
  }
  // PRODUCT-2237: submitted-name identity mismatch is a hard fail even when
  // the fraud score alone would pass or need review.
  if (identityFraudRisk.nameMismatch) {
    return "fail";
  }
  // PRODUCT-2164: manual verify clears the identity step for card chrome
  // (PM Deal Summary treats verified Needs Review as pass even before Persona).
  if (identityFraudRisk.isManuallyVerified) {
    return "pass";
  }
  // After SentiLink/IDV reset, keep pending while awaiting re-submission —
  // do not let a stale Lumen identity fail paint the card red mid-reset.
  if (identityFraudRisk.awaitingResubmission) {
    return "pending";
  }
  // PRODUCT-2429 follow-up (QA): header already keeps Lumen fail red when
  // Identity Risk is Needs Review; applicant card must match so managers do not
  // see a yellow applicant card under a red header for the same ID failure.
  if (lumenIdStatus === "fail") {
    return "fail";
  }
  if (identityFraudRisk.tierStatus === "pass") {
    return identityFraudRisk.governmentId.verified ? "pass" : "pending";
  }
  if (identityFraudRisk.tierStatus) {
    return identityFraudRisk.tierStatus;
  }
  return "pending";
};

// Create ID Verification card
export const createIdVerificationCard = (
  legacyApp: any,
  application: any,
  applicantLumenResult: any,
  applicationLumenResult: any,
  lumenRun: any,
  isLumenAvailable: boolean,
  backgroundReportIncomplete: boolean,
  basePath: string,
  workflowIncludesSentilink = false
) => {
  // Caller should check if identity is enabled before calling this function
  const sentilinkScopedLegacyApp = {
    ...legacyApp,
    sentilink: workflowIncludesSentilink ? legacyApp?.sentilink : undefined,
  };

  // Persona status takes precedence over stale Lumen outcomes after IDV reset
  if (
    isPersonaIncomplete(legacyApp?.personaStatus) &&
    legacyApp?.identity?.verified !== true
  ) {
    const idGroup = _.find(categoryGroups, { id: CategoryId.ID_VERIFICATION });

    const idVerificationModalDetails = getIdVerificationModalDetails(
      sentilinkScopedLegacyApp,
      application,
      "pending"
    );

    const identityFraudRisk = buildIdentityFraudRiskData(
      sentilinkScopedLegacyApp
    );

    return formatCardDetails({
      title: idGroup.name,
      status: (getIdCardChromeStatus(identityFraudRisk) ?? "pending") as StatusType,
      // When SentiLink data is present the in-card Identity Risk block
      // renders the status (tier, Government ID & Selfie); otherwise fall back to
      // the legacy flat verification line.
      details: identityFraudRisk
        ? []
        : [
            {
              label: "Verification Status",
              value: "Pending",
            },
          ],
      allDetailsLink: `${basePath}/${
        CATEGORY_ENDPOINTS_V2[CategoryId.ID_VERIFICATION]
      }`,
      cardModalDetails: idVerificationModalDetails,
      identityFraudRisk,
    });
  }

  const idVerificationStatus = getIssueStatus(
    "identity",
    "ID Verification",
    applicantLumenResult,
    applicationLumenResult,
    lumenRun,
    isLumenAvailable
  );

  const idStatus = determineCategoryStatus(
    CategoryId.ID_VERIFICATION,
    [idVerificationStatus],
    backgroundReportIncomplete,
    isLumenAvailable,
    lumenRun
  );

  const { documentDetails } = getIdentityApplicantSummary(legacyApp);
  const idGroup = _.find(categoryGroups, { id: CategoryId.ID_VERIFICATION });

  const idVerificationModalDetails = getIdVerificationModalDetails(
    sentilinkScopedLegacyApp,
    application,
    idStatus
  );
  const identityStatusLabel = legacyApp?.identity?.status;
  const isDuplicatePendingState =
    String(identityStatusLabel || "").toLowerCase() === "pending" &&
    String(documentDetails || "").toLowerCase() === "pending";

  const identityFraudRisk = buildIdentityFraudRiskData(sentilinkScopedLegacyApp);

  return formatCardDetails({
    title: idGroup.name,
    status: (getIdCardChromeStatus(identityFraudRisk, idStatus) ??
      idStatus) as StatusType,
    // When SentiLink data is present the in-card Identity Risk block
    // renders the status (tier, Government ID & Selfie); otherwise fall back to
    // the legacy flat verification line.
    details: identityFraudRisk
      ? []
      : [
          {
            label: isDuplicatePendingState
              ? "Verification Status"
              : identityStatusLabel || "Verification Status",
            value: isDuplicatePendingState ? "Pending" : documentDetails,
          },
        ],
    allDetailsLink: `${basePath}/${
      CATEGORY_ENDPOINTS_V2[CategoryId.ID_VERIFICATION]
    }`,
    cardModalDetails: idVerificationModalDetails,
    identityFraudRisk,
  });
};

// Create Income card
export const createIncomeCard = (
  legacyApp: any,
  applicantLumenResult: any,
  applicationLumenResult: any,
  lumenRun: any,
  isLumenAvailable: boolean,
  backgroundReportIncomplete: boolean,
  isSubmitted: boolean,
  basePath: string,
  enableMonthlyIncomeDisplay = false
) => {
  const incomeStatus = getIssueStatus(
    "income",
    "Income",
    applicantLumenResult,
    applicationLumenResult,
    lumenRun,
    isLumenAvailable
  );

  const { status: statusFromHistory } = getIncomeAndCashRunwayData(
    applicationLumenResult
  );

  const incomeCategoryStatus = determineCategoryStatus(
    CategoryId.INCOME,
    [incomeStatus, statusFromHistory],
    backgroundReportIncomplete,
    isLumenAvailable,
    lumenRun
  );

  const verifiedIncomeCents = legacyApp?.income?.verifiedIncome;
  const verifiedBalance = legacyApp?.balance?.balance;
  const incomeDisplay = formatVerifiedAnnualIncomeDisplay(
    verifiedIncomeCents,
    isSubmitted,
    enableMonthlyIncomeDisplay,
    formatIncomeBalanceDisplay
  );
  const monthlyIncomeDisplay = formatVerifiedMonthlyIncomeDisplay(
    verifiedIncomeCents,
    isSubmitted,
    enableMonthlyIncomeDisplay
  );
  const balanceDisplay = formatIncomeBalanceDisplay(
    verifiedBalance,
    "Balance",
    isSubmitted
  );
  const incomeGroup = _.find(categoryGroups, { id: CategoryId.INCOME });

  // Only show income detail status if it's a warning or failure
  const detailStatus =
    incomeCategoryStatus === "warn" || incomeCategoryStatus === "fail"
      ? incomeCategoryStatus
      : null;

  const incomeModalDetails = getIncomeModalDetails(
    legacyApp,
    incomeDisplay,
    balanceDisplay,
    incomeCategoryStatus
  );

  return formatCardDetails({
    title: incomeGroup.name,
    status: incomeCategoryStatus as StatusType,
    details: [
      {
        label: "Total Income",
        value: incomeDisplay,
        status: detailStatus,
      },
      ...(monthlyIncomeDisplay != null
        ? [
            {
              label: "Monthly Income",
              value: monthlyIncomeDisplay,
              status: detailStatus,
            },
          ]
        : []),
      {
        label: verifiedBalance != null ? "Cash Balance" : "",
        value: balanceDisplay,
        status: detailStatus,
      },
    ],
    allDetailsLink: `${basePath}/${CATEGORY_ENDPOINTS_V2[CategoryId.INCOME]}`,
    cardModalDetails: incomeModalDetails,
  });
};

// Create Credit History card
export const createCreditHistoryCard = (
  application: any,
  applicantLumenResult: any,
  applicationLumenResult: any,
  lumenRun: any,
  isLumenAvailable: boolean,
  isBackgroundCreditCheckSkipped: boolean,
  backgroundReportIncomplete: boolean,
  basePath: string,
  canAccessSensitiveDealDataInLumen: boolean,
  useSplitBackgroundTabs: boolean
) => {
  const noBackgroundReportStatus = getIssueStatus(
    "background",
    "No Background Report",
    applicantLumenResult,
    applicationLumenResult,
    lumenRun,
    isLumenAvailable
  );

  const backgroundCheckDetailsLink = buildApplicationCategoryDetailsLink(
    basePath,
    CategoryId.CREDIT_HISTORY,
    { useSplitBackgroundTabs }
  );

  const lowCreditScoreStatus = getIssueStatus(
    "credit",
    "Low Credit Score",
    applicantLumenResult,
    applicationLumenResult,
    lumenRun,
    isLumenAvailable
  );

  const thinCreditFileStatus = getIssueStatus(
    "credit",
    "Thin Credit File",
    applicantLumenResult,
    applicationLumenResult,
    lumenRun,
    isLumenAvailable
  );

  const bankruptciesStatus = getIssueStatus(
    "credit",
    "Bankruptcies",
    applicantLumenResult,
    applicationLumenResult,
    lumenRun,
    isLumenAvailable
  );

  const collectionsStatus = getIssueStatus(
    "credit",
    "Collections",
    applicantLumenResult,
    applicationLumenResult,
    lumenRun,
    isLumenAvailable
  );

  const foreclosuresStatus = getIssueStatus(
    "credit",
    "Foreclosures",
    applicantLumenResult,
    applicationLumenResult,
    lumenRun,
    isLumenAvailable
  );

  const creditStatus = determineCategoryStatus(
    CategoryId.CREDIT_HISTORY,
    [
      lowCreditScoreStatus,
      thinCreditFileStatus,
      bankruptciesStatus,
      collectionsStatus,
      foreclosuresStatus,
    ],
    backgroundReportIncomplete,
    isLumenAvailable,
    lumenRun
  );
  const isThinCreditFile =
    thinCreditFileStatus === "warn" || thinCreditFileStatus === "fail";
  const isLowCreditScore =
    lowCreditScoreStatus === "warn" || lowCreditScoreStatus === "fail";

  const creditGroup = _.find(categoryGroups, {
    id: CategoryId.CREDIT_HISTORY,
  });

  const creditScoreStatus = lowCreditScoreStatus || thinCreditFileStatus;

  const { creditScore, bankruptcies, collections, foreclosures } =
    getCreditApplicantSummary(
      applicantLumenResult,
      application,
      isLumenAvailable,
      isBackgroundCreditCheckSkipped,
      isThinCreditFile,
      isLowCreditScore
    );

  const creditHistoryModalDetails = getCreditHistoryModalDetails(
    applicantLumenResult,
    application,
    isLumenAvailable,
    isBackgroundCreditCheckSkipped,
    isLowCreditScore,
    isThinCreditFile,
    creditScoreStatus,
    bankruptciesStatus,
    collectionsStatus,
    foreclosuresStatus
  );

  return formatCardDetails({
    title: creditGroup.name,
    status: creditStatus as StatusType,
    details: [
      {
        label: "Credit Score",
        value: creditScore,
        status: creditScoreStatus || noBackgroundReportStatus,
      },
      {
        label: "Bankruptcies",
        value: bankruptcies,
        status: bankruptciesStatus || noBackgroundReportStatus,
      },
      {
        label: "Collections",
        value: collections,
        status: collectionsStatus || noBackgroundReportStatus,
      },
      {
        label: "Foreclosures",
        value: foreclosures,
        status: foreclosuresStatus || noBackgroundReportStatus,
      },
    ],
    // When user does not have permission to view background check details,
    // we don't provide the link to the credit-and-background-check
    // page since that page requires authentication and would be inaccessible.
    // This prevents users from seeing a "View all details" link that would lead nowhere.
    ...(!canAccessSensitiveDealDataInLumen || !backgroundCheckDetailsLink
      ? {}
      : {
          allDetailsLink: backgroundCheckDetailsLink,
        }),
    // When user does not have permission to view background check details,
    // we don't provide the modal details since the data is restricted.
    ...(!canAccessSensitiveDealDataInLumen
      ? {}
      : {
          cardModalDetails: creditHistoryModalDetails,
        }),
  });
};

// Expired report data must not be shown: one message row, no modal
export const createExpiredBackgroundCard = (
  categoryId: CategoryId,
  basePath: string,
  canAccessSensitiveDealDataInLumen: boolean,
  useSplitBackgroundTabs: boolean
) => {
  const backgroundCheckDetailsLink = buildApplicationCategoryDetailsLink(
    basePath,
    categoryId,
    { useSplitBackgroundTabs }
  );

  const group = _.find(categoryGroups, { id: categoryId });

  return formatCardDetails({
    title: group.name,
    status: "pending",
    details: [{ value: EXPIRED_BACKGROUND_REPORTS_MESSAGE }],
    ...(!canAccessSensitiveDealDataInLumen || !backgroundCheckDetailsLink
      ? {}
      : {
          allDetailsLink: backgroundCheckDetailsLink,
        }),
  });
};

// Create Rental History card
export const createRentalHistoryCard = (
  application: any,
  applicantLumenResult: any,
  applicationLumenResult: any,
  lumenRun: any,
  isLumenAvailable: boolean,
  isBackgroundCreditCheckSkipped: boolean,
  backgroundReportIncomplete: boolean,
  basePath: string,
  canAccessSensitiveDealDataInLumen: boolean,
  useSplitBackgroundTabs: boolean
) => {
  const noBackgroundReportStatus = getIssueStatus(
    "background",
    "No Background Report",
    applicantLumenResult,
    applicationLumenResult,
    lumenRun,
    isLumenAvailable
  );

  const backgroundCheckDetailsLink = buildApplicationCategoryDetailsLink(
    basePath,
    CategoryId.RENTAL_HISTORY,
    { useSplitBackgroundTabs }
  );

  const evictionsStatus = getIssueStatus(
    "rental",
    "Evictions",
    applicantLumenResult,
    applicationLumenResult,
    lumenRun,
    isLumenAvailable
  );

  const rentalCollectionsStatus = getIssueStatus(
    "rental",
    "Rental Collections",
    applicantLumenResult,
    applicationLumenResult,
    lumenRun,
    isLumenAvailable
  );

  const rentalStatus = determineCategoryStatus(
    CategoryId.RENTAL_HISTORY,
    [evictionsStatus, rentalCollectionsStatus],
    backgroundReportIncomplete,
    isLumenAvailable,
    lumenRun
  );

  const rentalGroup = _.find(categoryGroups, {
    id: CategoryId.RENTAL_HISTORY,
  });

  const { evictionRecords, rentalCollections } = getRentalApplicantSummary(
    applicantLumenResult,
    application,
    isLumenAvailable,
    isBackgroundCreditCheckSkipped
  );

  const rentalHistoryModalDetails = getRentalHistoryModalDetails(
    applicantLumenResult,
    application,
    isLumenAvailable,
    isBackgroundCreditCheckSkipped,
    evictionsStatus,
    rentalCollectionsStatus
  );

  return formatCardDetails({
    title: rentalGroup.name,
    status: rentalStatus as StatusType,
    details: [
      {
        label: "Eviction Records",
        value: evictionRecords,
        status: evictionsStatus || noBackgroundReportStatus,
      },
      {
        label: "Rental Collections",
        value: rentalCollections,
        status: rentalCollectionsStatus || noBackgroundReportStatus,
      },
    ],
    // When user does not have permission to view background check details,
    // we don't provide the link to the credit-and-background-check
    // page since that page requires authentication and would be inaccessible.
    // This prevents users from seeing a "View all details" link that would lead nowhere.
    ...(!canAccessSensitiveDealDataInLumen || !backgroundCheckDetailsLink
      ? {}
      : {
          allDetailsLink: backgroundCheckDetailsLink,
        }),
    // When user does not have permission to view background check details,
    // we don't provide the modal details since the data is restricted.
    ...(!canAccessSensitiveDealDataInLumen
      ? {}
      : {
          cardModalDetails: rentalHistoryModalDetails,
        }),
  });
};

// Create Criminal History card
export const createCriminalHistoryCard = (
  legacyApp: any,
  application: any,
  applicantLumenResult: any,
  applicationLumenResult: any,
  lumenRun: any,
  isLumenAvailable: boolean,
  isBackgroundCreditCheckSkipped: boolean,
  backgroundReportIncomplete: boolean,
  basePath: string,
  canAccessSensitiveDealDataInLumen: boolean,
  useSplitBackgroundTabs: boolean
) => {
  const noBackgroundReportStatus = getIssueStatus(
    "background",
    "No Background Report",
    applicantLumenResult,
    applicationLumenResult,
    lumenRun,
    isLumenAvailable
  );

  const backgroundCheckDetailsLink = buildApplicationCategoryDetailsLink(
    basePath,
    CategoryId.CRIMINAL_HISTORY,
    { useSplitBackgroundTabs }
  );

  const criminalRecordsStatus = getIssueStatus(
    "criminal",
    "Criminal Records",
    applicantLumenResult,
    applicationLumenResult,
    lumenRun,
    isLumenAvailable
  );

  const feloniesStatus = getIssueStatus(
    "criminal",
    "Felonies",
    applicantLumenResult,
    applicationLumenResult,
    lumenRun,
    isLumenAvailable
  );

  const criminalStatus = determineCategoryStatus(
    CategoryId.CRIMINAL_HISTORY,
    [criminalRecordsStatus, feloniesStatus],
    backgroundReportIncomplete,
    isLumenAvailable,
    lumenRun
  );

  const hasConditionallyProceedEnabled =
    legacyApp?.info?.hasConditionallyProceedEnabled ??
    legacyApp?.hasConditionallyProceedEnabled;
  const isConditionallyProceeded =
    legacyApp?.info?.isConditionallyProceeded ??
    legacyApp?.isConditionallyProceeded;
  const isConditionalProceedPending =
    hasConditionallyProceedEnabled && !isConditionallyProceeded;

  if (isConditionalProceedPending) {
    const criminalGroup = _.find(categoryGroups, {
      id: CategoryId.CRIMINAL_HISTORY,
    });

    return formatCardDetails({
      title: criminalGroup.name,
      status: "pending" as StatusType,
      details: [
        {
          label: "Criminal Records",
          value: "Pending",
        },
      ],
      ...(!canAccessSensitiveDealDataInLumen || !backgroundCheckDetailsLink
        ? {}
        : {
            allDetailsLink: backgroundCheckDetailsLink,
            cardModalDetails: getCriminalHistoryModalDetails(
              application,
              isBackgroundCreditCheckSkipped
            ),
          }),
    });
  }

  const { criminalRecords } = getCriminalApplicantSummary(
    application,
    isBackgroundCreditCheckSkipped,
    isConditionalProceedPending
  );

  const criminalGroup = _.find(categoryGroups, {
    id: CategoryId.CRIMINAL_HISTORY,
  });

  const criminalHistoryModalDetails = getCriminalHistoryModalDetails(
    application,
    isBackgroundCreditCheckSkipped
  );

  return formatCardDetails({
    title: criminalGroup.name,
    status: criminalStatus as StatusType,
    details: [
      {
        label: "Criminal Records",
        value: criminalRecords,
        status: criminalRecordsStatus || noBackgroundReportStatus,
      },
    ],
    // When user does not have permission to view background check details,
    // we don't provide the link to the credit-and-background-check
    // page since that page requires authentication and would be inaccessible.
    // This prevents users from seeing a "View all details" link that would lead nowhere.
    ...(!canAccessSensitiveDealDataInLumen || !backgroundCheckDetailsLink
      ? {}
      : {
          allDetailsLink: backgroundCheckDetailsLink,
        }),
    // When user does not have permission to view background check details,
    // we don't provide the modal details since the data is restricted.
    ...(!canAccessSensitiveDealDataInLumen
      ? {}
      : {
          cardModalDetails: criminalHistoryModalDetails,
        }),
  });
};

// Main function to get all card details by category
export const getCardDetailsByCategory = (
  lumenRun: any,
  legacyApp: any,
  application: any,
  applicantLumenResult: any,
  applicationLumenResult: any,
  isLumenAvailable: boolean,
  isSubmitted: boolean,
  showBackgroundCreditSection: boolean,
  canAccessSensitiveDealDataInLumen: boolean,
  useSplitBackgroundTabs: boolean,
  enableMonthlyIncomeDisplay = false,
  workflowIncludesSentilink = false
): Record<string, ApplicantCardDetailsProps> => {
  const cardDetailsByCategory: Record<string, ApplicantCardDetailsProps> = {};
  const basePath = `/applications/${legacyApp?.appId}`;
  const backgroundReportIncomplete = isBackgroundReportIncomplete(lumenRun);
  const isBackgroundCreditCheckSkipped = legacyApp?.credit?.skipped;

  // ID Verification card - only create if identity data exists
  if (legacyApp?.identity?.enabled) {
    const idVerificationCard = createIdVerificationCard(
      legacyApp,
      application,
      applicantLumenResult,
      applicationLumenResult,
      lumenRun,
      isLumenAvailable,
      backgroundReportIncomplete,
      basePath,
      workflowIncludesSentilink
    );
    if (idVerificationCard) {
      cardDetailsByCategory[CategoryId.ID_VERIFICATION] = idVerificationCard;
    }
  }

  // Income card - only create if income or balance data exists
  if (legacyApp?.income || legacyApp?.balance) {
    cardDetailsByCategory[CategoryId.INCOME] = createIncomeCard(
      legacyApp,
      applicantLumenResult,
      applicationLumenResult,
      lumenRun,
      isLumenAvailable,
      backgroundReportIncomplete,
      isSubmitted,
      basePath,
      enableMonthlyIncomeDisplay
    );
  }

  // Background check sections
  if (
    showBackgroundCreditSection &&
    hasExpiredBackgroundReports(application?.backgroundCreditCheck)
  ) {
    [
      CategoryId.CREDIT_HISTORY,
      CategoryId.RENTAL_HISTORY,
      CategoryId.CRIMINAL_HISTORY,
    ].forEach((categoryId) => {
      cardDetailsByCategory[categoryId] = createExpiredBackgroundCard(
        categoryId,
        basePath,
        canAccessSensitiveDealDataInLumen,
        useSplitBackgroundTabs
      );
    });
  } else if (showBackgroundCreditSection) {
    // Credit History card
    cardDetailsByCategory[CategoryId.CREDIT_HISTORY] = createCreditHistoryCard(
      application,
      applicantLumenResult,
      applicationLumenResult,
      lumenRun,
      isLumenAvailable,
      isBackgroundCreditCheckSkipped,
      backgroundReportIncomplete,
      basePath,
      canAccessSensitiveDealDataInLumen,
      useSplitBackgroundTabs
    );

    // Rental History card
    cardDetailsByCategory[CategoryId.RENTAL_HISTORY] = createRentalHistoryCard(
      application,
      applicantLumenResult,
      applicationLumenResult,
      lumenRun,
      isLumenAvailable,
      isBackgroundCreditCheckSkipped,
      backgroundReportIncomplete,
      basePath,
      canAccessSensitiveDealDataInLumen,
      useSplitBackgroundTabs
    );

    // Criminal History card
    cardDetailsByCategory[CategoryId.CRIMINAL_HISTORY] =
      createCriminalHistoryCard(
        legacyApp,
        application,
        applicantLumenResult,
        applicationLumenResult,
        lumenRun,
        isLumenAvailable,
        isBackgroundCreditCheckSkipped,
        backgroundReportIncomplete,
        basePath,
        canAccessSensitiveDealDataInLumen,
        useSplitBackgroundTabs
      );
  }

  return cardDetailsByCategory;
};
